/**
 * FieldHelp — a small "(i)" info affordance for form field labels.
 *
 * Click/tap the icon to open a popover explaining what the field does and,
 * for selects, what each option means. Rich content + touch-friendly (vs a
 * hover tooltip). Place it as a *sibling* of the <label>, not a child, so
 * clicking the icon never focuses/opens the associated control.
 */

import { Info } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

export interface FieldHelpOption {
  label: string
  description: string
}

export interface FieldHelpContent {
  /** Short field title (also used for the trigger's accessible name). */
  title: string
  /** One or two sentences: what the field does + its operational implication. */
  summary: string
  /** For selects — one entry per option, in display order. */
  options?: FieldHelpOption[]
  /** Optional caveat / dependency / example. */
  note?: string
}

interface FieldHelpProps {
  content: FieldHelpContent
}

export function FieldHelp({ content }: FieldHelpProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`About: ${content.title}`}
          className="inline-flex shrink-0 rounded-full text-muted-foreground/60 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-80 space-y-2">
        <p className="text-sm font-medium leading-none">{content.title}</p>
        <p className="text-xs text-muted-foreground">{content.summary}</p>
        {content.options && content.options.length > 0 && (
          <ul className="space-y-1.5 pt-1">
            {content.options.map((o) => (
              <li key={o.label} className="text-xs leading-snug">
                <span className="font-medium text-foreground">{o.label}</span>
                <span className="text-muted-foreground"> — {o.description}</span>
              </li>
            ))}
          </ul>
        )}
        {content.note && (
          <p className="border-t pt-2 text-xs text-muted-foreground">{content.note}</p>
        )}
      </PopoverContent>
    </Popover>
  )
}
